import axios from 'axios';
import { DEBUG_SKIP_AUTH } from '../config/debug';

const apiBaseURL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'development' ? 'http://localhost:5000' : '');

const api = axios.create({
  baseURL: apiBaseURL,
  timeout: 30000,
});

const TOKEN_REFRESH_BUFFER_MS = 2 * 60 * 1000;

let redirectingToLogin = false;
let refreshPromise = null;

function shouldRefreshSession(error) {
  const status = error.response?.status;
  const code = error.response?.data?.code;
  return status === 401 || (status === 403 && code === 'TOKEN_EXPIRED');
}

function getRequestUrl(config) {
  if (!config) return '';
  const url = config.url || '';
  if (url.startsWith('http')) return url;
  const base = config.baseURL || api.defaults.baseURL || '';
  return `${base}${url}`;
}

function isAuthRequest(config) {
  if (!config) return false;
  if (config.skipAuthRefresh) return true;
  return /\/api\/auth\//.test(getRequestUrl(config));
}

function getAccessTokenExpiryMs(accessToken) {
  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1]));
    return payload.exp * 1000;
  } catch {
    return 0;
  }
}

function isAccessTokenExpiringSoon(accessToken) {
  if (!accessToken) return true;
  const expiresAtMs = getAccessTokenExpiryMs(accessToken);
  if (!expiresAtMs) return true;
  return Date.now() >= expiresAtMs - TOKEN_REFRESH_BUFFER_MS;
}

export function forceLogin() {
  if (redirectingToLogin) return;
  redirectingToLogin = true;

  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  delete api.defaults.headers.common.Authorization;

  const destination = DEBUG_SKIP_AUTH ? '/' : '/login';
  if (window.location.pathname !== destination) {
    window.location.replace(destination);
  }
}

/** Refresh access token without triggering the session-retry interceptor. */
export async function refreshAccessTokenRequest() {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token');
    }

    const { data } = await api.post(
      '/api/auth/refresh',
      { refreshToken },
      { skipAuthRefresh: true }
    );

    if (!data?.accessToken) {
      throw new Error('Refresh response missing accessToken');
    }

    localStorage.setItem('accessToken', data.accessToken);
    api.defaults.headers.common.Authorization = `Bearer ${data.accessToken}`;

    window.dispatchEvent(new CustomEvent('mediablox:session-refreshed'));

    return data.accessToken;
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

/** Proactively refresh an expiring access token while the user is still logged in. */
export async function ensureAccessTokenFresh() {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    return null;
  }

  const accessToken = localStorage.getItem('accessToken');
  if (!accessToken || isAccessTokenExpiringSoon(accessToken)) {
    return refreshAccessTokenRequest();
  }

  return accessToken;
}

api.interceptors.request.use(
  async (config) => {
    if (!isAuthRequest(config)) {
      try {
        await ensureAccessTokenFresh();
      } catch {
        // Allow the request through; the response interceptor handles auth recovery.
      }
      const accessToken = localStorage.getItem('accessToken');
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      !shouldRefreshSession(error) ||
      !originalRequest ||
      originalRequest._retry ||
      isAuthRequest(originalRequest)
    ) {
      return Promise.reject(error);
    }

    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      forceLogin();
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      const newAccessToken = await refreshAccessTokenRequest();
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      return api(originalRequest);
    } catch (refreshError) {
      forceLogin();
      return Promise.reject(refreshError);
    }
  }
);

export default api;
